import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Row, Col, Divider } from 'antd';
import { LockOutlined, SafetyOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import api from '../../../services/api';

const SecuritySettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const { user } = useSelector(state => state.auth);

    const handleChangePassword = async (values) => {
        try {
            setLoading(true);
            
            const response = await api.put('/users/change-password', {
                currentPassword: values.currentPassword,
                newPassword: values.newPassword
            });

            if (response.data.success) {
                message.success('Password changed successfully!');
                form.resetFields();
            } else {
                message.error(response.data.message || 'Failed to change password!');
            }
        } catch (error) {
            console.error('Error changing password:', error);
            if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error('An error occurred while changing password!');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '24px' }}>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card
                        title={
                            <span>
                                <LockOutlined style={{ marginRight: 8 }} />
                                Change Password
                            </span>
                        }
                        style={{ marginBottom: 24 }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleChangePassword}
                            style={{ maxWidth: 400 }}
                        >
                            <Form.Item
                                label="Current Password"
                                name="currentPassword"
                                rules={[
                                    { required: true, message: 'Please enter your current password!' }
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Enter current password"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                label="New Password"
                                name="newPassword"
                                rules={[
                                    { required: true, message: 'Please enter new password!' },
                                    { min: 6, message: 'Password must be at least 6 characters!' }
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder="Enter new password"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Confirm New Password"
                                name="confirmPassword"
                                dependencies={['newPassword']}
                                rules={[
                                    { required: true, message: 'Please confirm your new password!' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Password confirmation does not match!'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<SafetyOutlined />}
                                    placeholder="Confirm new password"
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                    style={{ width: '100%' }}
                                >
                                    Change Password
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                <Col span={24}>
                    <Card
                        title={
                            <span>
                                <SafetyOutlined style={{ marginRight: 8 }} />
                                Account Information
                            </span>
                        }
                    >
                        <div style={{ padding: '16px 0' }}>
                            <Row gutter={[16, 16]}>
                                <Col span={6}>
                                    <strong>Username:</strong>
                                </Col>
                                <Col span={18}>
                                    {user?.username || 'N/A'}
                                </Col>
                                
                                <Col span={6}>
                                    <strong>Email:</strong>
                                </Col>
                                <Col span={18}>
                                    {user?.email || 'N/A'}
                                </Col>
                                
                                <Col span={6}>
                                    <strong>Full Name:</strong>
                                </Col>
                                <Col span={18}>
                                    {user?.fullName || 'N/A'}
                                </Col>
                                
                                <Col span={6}>
                                    <strong>Role:</strong>
                                </Col>
                                <Col span={18}>
                                    {user?.role || 'N/A'}
                                </Col>
                            </Row>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SecuritySettings;
